import { ART_PALETTE } from '../art-palette.js';
import { MASK, PIPE_MASKS } from './masks.js';

/**
 * Elevation family: cliff rims/faces, ramps, stairs, height overlay.
 *
 * Lighting is upper-left throughout. Transitions are authored per orientation
 * so stairs keep tread direction and ramps keep head/foot slope language;
 * pure rotateClockwise is not used for those families.
 *
 * Seam rules:
 * - Cliff wall without caps: col0 == col15 (horizontal runs).
 * - Cliff multi-band: row15 of upper band == row0 of lower band.
 * - Transition segments: shared edge columns/rows match exactly on parallel runs.
 */

// --- cliff wall (projected rock faces) --------------------------------------

function cliffFaceVariants() {
  return Object.fromEntries(PIPE_MASKS.map((mask) => {
    // Low two bits select single/top/middle/bottom. Bits 4/8 are L/R end caps.
    const band = mask & 3;
    const leftCap = Boolean(mask & 4);
    const rightCap = Boolean(mask & 8);
    const hasTop = band === 0 || band === 1;
    const hasBottom = band === 0 || band === 3;

    // One large flat face (§0.3/§0.4). The old art shaded the face by x, which
    // made a long cliff run repeat light→dark every 16px and printed the tile
    // grid across the whole wall. The body is uniform now; the only value
    // changes are the lit top lip, the dark base contact, and a few cracks.
    const pixels = Array.from({ length: 16 }, () => Array(16).fill(5));

    // Structural jointing: two short vertical strokes one ramp step down from
    // the face. Straight strokes read as rock joints; the stepped, diagonal
    // version read as scattered speckle and printed the 16px cell at 1×.
    // The phase alternates with the band so stacked faces do not stripe.
    const joints = band % 2 === 0 ? [[4, 4, 4], [11, 8, 4]] : [[6, 3, 4], [12, 9, 4]];
    for (const [x, y0, height] of joints) {
      for (let y = y0; y < y0 + height; y++) pixels[y][x] = 4;
    }

    if (hasTop) {
      // Sun lip where the plateau turns down into the face.
      for (let x = 0; x < 16; x++) { pixels[0][x] = 6; pixels[1][x] = 6; }
    }
    if (hasBottom) {
      // Contact band where the face meets the ground below.
      for (let x = 0; x < 16; x++) { pixels[14][x] = 4; pixels[15][x] = 3; }
    }

    // Horizontal runs join col15 → col0 of the same mask, and stacked bands join
    // row15 → row0: every one of those lines is a uniform fill, so both hold.
    // End caps: left lit (the face turns toward the light), right in shadow.
    if (leftCap) {
      for (let y = 0; y < (hasBottom ? 14 : 16); y++) pixels[y][0] = 6;
    }
    if (rightCap) {
      for (let y = 0; y < (hasBottom ? 14 : 16); y++) pixels[y][15] = 4;
    }

    return [mask, pixels];
  }));
}

// --- cliff ground (plateau rims) ---------------------------------------------

function cliffTopVariants() {
  return Object.fromEntries(PIPE_MASKS.map((mask) => {
    const pixels = Array.from({ length: 16 }, () => Array(16).fill(0));
    const north = Boolean(mask & MASK.N);
    const east = Boolean(mask & MASK.E);
    const south = Boolean(mask & MASK.S);
    const west = Boolean(mask & MASK.W);

    // 2px rims in the cliff ramp only — no sand-coast accents on rock edges.
    // Every rim is uniform along its run: any x- or y-dependent shading here
    // repeats every 16px along a long rim and draws the tile grid.
    // UL light: the far (north) edge recedes into shadow, the west face is lit.
    if (north) {
      for (let x = west ? 1 : 0; x < 16 - (east ? 1 : 0); x++) {
        pixels[0][x] = 4;
        pixels[1][x] = 6;
      }
    }
    if (south) {
      for (let x = west ? 1 : 0; x < 16 - (east ? 1 : 0); x++) {
        pixels[14][x] = 5;
        pixels[15][x] = 4;
      }
    }
    if (west) {
      for (let y = north ? 1 : 0; y < 16 - (south ? 1 : 0); y++) {
        // Lit side: lighter outer, mid inner (not a shadow bevel).
        pixels[y][0] = 6;
        pixels[y][1] = 5;
      }
    }
    if (east) {
      for (let y = north ? 1 : 0; y < 16 - (south ? 1 : 0); y++) {
        pixels[y][14] = 5;
        pixels[y][15] = 4;
      }
    }

    // Pixel-rounded outside corners join orthogonal rims without square spikes.
    if (north && west) {
      pixels[0][0] = 0;
      pixels[1][0] = 4;
      pixels[0][1] = 4;
    }
    if (north && east) {
      pixels[0][15] = 0;
      pixels[1][15] = 4;
      pixels[0][14] = 4;
    }
    if (south && west) {
      pixels[15][0] = 0;
      pixels[14][0] = 4;
      pixels[15][1] = 3;
    }
    if (south && east) {
      pixels[15][15] = 0;
      pixels[14][15] = 4;
      pixels[15][14] = 3;
    }
    return [mask, pixels];
  }));
}

const CLIFF_ART = {
  key: 'cliff.rock-face', name: 'Rock cliff face', channel: 'cliff', mode: 'pipe4',
  connectionGroup: 'elevation', palette: ART_PALETTE, collision: { wall: true },
  components: { ground: cliffTopVariants(), wall: cliffFaceVariants() },
};

// --- height overlay ---------------------------------------------------------

function heightMarkerPixels(count) {
  const pixels = Array.from({ length: 16 }, () => Array(16).fill(0));
  // Stacked bars, one per level: a diagnostic reads faster as a height gauge
  // than as a row of glyphs, and stacking counts itself at 1×.
  for (let level = 0; level < count; level++) {
    const top = 12 - level * 4;
    for (let x = 3; x <= 12; x++) { pixels[top - 1][x] = 1; pixels[top + 2][x] = 1; }
    for (let y = top; y <= top + 1; y++) {
      pixels[y][3] = 1;
      pixels[y][12] = 1;
      for (let x = 4; x <= 11; x++) pixels[y][x] = 11;
    }
  }
  return pixels;
}

const HEIGHT_OVERLAY_ART = {
  key: 'diagnostic.height', name: 'Height overlay', channel: 'heightOverlay', mode: 'pipe4',
  connectionGroup: 'height-overlay', palette: ART_PALETTE, collision: {},
  components: { roof: { 1: heightMarkerPixels(1), 2: heightMarkerPixels(2), 3: heightMarkerPixels(3) } },
};

// --- shared transition segment geometry -------------------------------------

/** Segment bits: bit0 = parallel-negative neighbor, bit1 = parallel-positive.
 *  N/S orients: W/E. E/W orients: N/S. */
function segmentSpan(segment) {
  const connectsA = Boolean(segment & 1);
  const connectsB = Boolean(segment & 2);
  return {
    connectsA,
    connectsB,
    start: connectsA ? 0 : 3,
    end: connectsB ? 16 : 13,
  };
}

/**
 * Ramp surface value `along` pixels down from the head (0) to the foot (15).
 * Flat earth ramp: lit head, body, shadowed foot, plus a tread separator every
 * 4px so the slope reads as climbable rather than as a poured gradient.
 * Uniform across the cross-axis, so shared segment edges always match.
 */
function rampSlopeColor(along) {
  if (along === 15) return 3; // dark contact line at the foot
  const body = along < 3 ? 9 : along < 11 ? 8 : 7;
  if (along % 4 !== 3) return body;
  return body === 9 ? 8 : body === 8 ? 7 : 3; // tread separator, one step down
}

// --- ramps (orientation-authored slope) -------------------------------------

/**
 * Dirt ramp: lit head, mid body, shadowed foot, 4px treads, and a dark contact
 * line where it meets the lower ground. Cross-axis value is uniform and only
 * free ends take a cap, so parallel-run seams stay exact.
 */
function rampPixels(orientation, segment) {
  const pixels = Array.from({ length: 16 }, () => Array(16).fill(0));
  const { connectsA, connectsB, start, end } = segmentSpan(segment);
  const vertical = orientation === 0 || orientation === 2;
  const highIsLowIndex = orientation === 0 || orientation === 3;
  const footIndex = highIsLowIndex ? 15 : 0;

  if (vertical) {
    for (let y = 0; y < 16; y++) {
      const body = rampSlopeColor(highIsLowIndex ? y : 15 - y);
      for (let x = start; x < end; x++) pixels[y][x] = body;
      // Free side edges (UL stable): A-side softer, B-side darker.
      if (!connectsA) pixels[y][start] = 7;
      if (!connectsB) pixels[y][end - 1] = 3;
    }
    // Contact line runs the full width, over the side edges.
    for (let x = start; x < end; x++) pixels[footIndex][x] = 3;
  } else {
    for (let x = 0; x < 16; x++) {
      const body = rampSlopeColor(highIsLowIndex ? x : 15 - x);
      for (let y = start; y < end; y++) pixels[y][x] = body;
      if (!connectsA) pixels[start][x] = 7;
      if (!connectsB) pixels[end - 1][x] = 3;
    }
    for (let y = start; y < end; y++) pixels[y][footIndex] = 3;
  }
  return pixels;
}

function orientedRampVariants() {
  const variants = {};
  for (let orientation = 0; orientation < 4; orientation++) {
    for (let segment = 0; segment < 4; segment++) {
      variants[orientation * 4 + segment] = rampPixels(orientation, segment);
    }
  }
  return variants;
}

// --- stairs (orientation-authored treads) -----------------------------------

/**
 * Step cadence is always perpendicular to ascent:
 *   N/S → horizontal treads (rows)
 *   E/W → vertical treads (columns)
 * Cross-axis is uniform (plus free-end caps) so segment seams stay exact.
 * Materials use stone 24–27 so stairs read as built structure.
 */
/**
 * Four-pixel steps: lit nosing, two rows of tread, one row of riser shadow.
 * Three stone values plus the contact band — no per-step gradient.
 */
function stairStepColor(distFromHigh) {
  if (distFromHigh === 15) return 24; // contact line at the foot
  if (distFromHigh < 2) return 27;    // lit landing at the head
  if (distFromHigh % 4 === 0) return 27; // nosing
  if (distFromHigh % 4 === 3) return 25; // riser shadow
  return 26; // tread
}

function stairPixels(orientation, segment) {
  const pixels = Array.from({ length: 16 }, () => Array(16).fill(0));
  const { connectsA, connectsB, start, end } = segmentSpan(segment);
  const vertical = orientation === 0 || orientation === 2;
  const highIsLowIndex = orientation === 0 || orientation === 3;
  const footIndex = highIsLowIndex ? 15 : 0;

  if (vertical) {
    for (let y = 0; y < 16; y++) {
      const c = stairStepColor(highIsLowIndex ? y : 15 - y);
      for (let x = start; x < end; x++) pixels[y][x] = c;
      // Free stringers on open sides only.
      if (!connectsA) pixels[y][start] = 25;
      if (!connectsB) pixels[y][end - 1] = 24;
    }
    for (let x = start; x < end; x++) pixels[footIndex][x] = 24;
  } else {
    for (let x = 0; x < 16; x++) {
      const c = stairStepColor(highIsLowIndex ? x : 15 - x);
      for (let y = start; y < end; y++) pixels[y][x] = c;
      if (!connectsA) pixels[start][x] = 25;
      if (!connectsB) pixels[end - 1][x] = 24;
    }
    for (let y = start; y < end; y++) pixels[y][footIndex] = 24;
  }
  return pixels;
}

function orientedStairVariants() {
  const variants = {};
  for (let orientation = 0; orientation < 4; orientation++) {
    for (let segment = 0; segment < 4; segment++) {
      variants[orientation * 4 + segment] = stairPixels(orientation, segment);
    }
  }
  return variants;
}

const RAMP_ART = {
  key: 'transition.ramp', name: 'Ramp', channel: 'transition', mode: 'pipe4', transitionKind: 'ramp',
  connectionGroup: 'ramp', palette: ART_PALETTE, collision: {},
  components: { ground: orientedRampVariants() },
};

const STAIR_ART = {
  key: 'transition.stairs', name: 'Stairs', channel: 'transition', mode: 'pipe4', transitionKind: 'stairs',
  connectionGroup: 'stairs', palette: ART_PALETTE, collision: {},
  components: { ground: orientedStairVariants() },
};

export { CLIFF_ART, HEIGHT_OVERLAY_ART, RAMP_ART, STAIR_ART };
